import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import Picker from '../../picker/Picker';
import ToggleButton from '@mui/material/ToggleButton';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import useSnackbar from '../../hooks/useSnackbar';
import CreateTagDialog from '../../account/tags/dialogs/CreateTagDialog';
import Tooltip from '@mui/material/Tooltip';
import ArchiveIcon from '@mui/icons-material/Archive';
import LoadingOverlay from '../../loadingOverlay/LoadingOverlay';
import { format } from 'date-fns';
import Chip from '@mui/material/Chip';
import useSuspenseFetch from '../../hooks/useSuspenseFetch';
import { usePutMutation } from '../../hooks/useAuthenticatedMutation';

const nameRegex = /[a-zA-Z0-9\s-]/g;

const toggleIcon = {
    marginRight: 1
};

const EditGroupDialog = ({ group, onClose }) => {
    const { data: groups } = useSuspenseFetch('groups');
    const { data: tags } = useSuspenseFetch('tags');

    const groupPutMutation = usePutMutation(`groups/${group.id}`, 'groups');

    const [showDialog, setShowDialog] = useState(false);
    const [type, setType] = useState(group.type ?? 'private');
    const [name, setName] = useState(group.name ?? '');
    const [description, setDescription] = useState(group.description ?? '');
    const [isValid, setIsValid] = useState(false);
    const [selectedTags, setSelectedTags] = useState(group.tags ?? []);
    const [isInitialising, setIsInitialising] = useState(true);
    const { showSnackbar } = useSnackbar();

    const handleSubmit = e => {
        const updatedGroup = {
            id: group.id,
            name,
            description,
            ...(selectedTags && { tags: selectedTags }),
            ...(type && { type })
        };

        e.preventDefault();

        groupPutMutation.mutate(
            updatedGroup,
            {
                onSuccess: showSnackbar('Changes saved', 'success'),
                onError: (error) => {
                    showSnackbar('Error: Group not saved', 'error');
                    console.error(error);
                }
            }
        );

        onClose();
    };

    const copyToClipboard = value => {
        navigator.clipboard.writeText(value);
        showSnackbar('Group ID copied to clipboard', 'info');
    };

    useEffect(() => {
        if (groups) {
            setIsInitialising(false);
        }
    }, [groups]);

    useEffect(() => {
        if (name.trim() !== '' && (name.match(nameRegex)?.length === name.length)) {
            const existingGroup = groups?.find(e => e.name.trim().toLowerCase() === name.trim().toLowerCase() && e.id !== group.id);
            setIsValid(!existingGroup);
        }
        else {
            setIsValid(false);
        }

        if (name.length > 100) {
            setIsValid(false);
        }
    }, [name, group.id, groups]);

    if (isInitialising) {
        return (
            <LoadingOverlay />
        );
    }

    return (
        <>
            <Dialog onClose={onClose} open={true}>
                <DialogTitle sx={{ pb: 0 }}>Edit Group</DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent>
                        <Stack spacing={2}>
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                <Chip
                                    label={(
                                        <span>
                                            Group ID: <strong>{group.id}</strong>
                                        </span>
                                    )}
                                    variant="outlined"
                                    onDelete={() => copyToClipboard(group.id)}
                                    deleteIcon={<ContentCopyIcon />}
                                    onClick={() => copyToClipboard(group.id)}
                                />
                                <Chip
                                    label={`Created: ${group.created ? format(new Date(group.created), 'dd/MM/yyyy HH:mm') : '–'}`}
                                    variant="outlined"
                                />
                            </Stack>
                            <TextField
                                autoFocus
                                id="name"
                                label="Name"
                                type="text"
                                fullWidth
                                value={name}
                                required
                                onChange={e => setName(e.target.value)}
                                margin="none"
                                error={!isValid}
                                helperText="Group names must be unique and be less than 100 characters. Alphanumeric characters, hyphens and spaces are allowed."
                            />
                            <TextField
                                id="description"
                                label="Description"
                                type="text"
                                fullWidth
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                margin="none"
                            />
                            <Alert severity="info">
                                Group Name and Description are shown to your contacts in the list of Public Groups in Manage Preferences, accessible via a link in your campaigns.
                            </Alert>
                            <Typography>
                                Type
                            </Typography>
                            <ToggleButtonGroup
                                value={type}
                                exclusive
                                onChange={(e, type) => setType(type)}
                                aria-label="type"
                                color="primary"
                            >
                                <ToggleButton value="private" aria-label="private">
                                    <VisibilityOffIcon sx={toggleIcon} />
                                    Private
                                </ToggleButton>
                                <ToggleButton value="public" aria-label="public">
                                    <VisibilityIcon sx={toggleIcon} />
                                    Public
                                </ToggleButton>
                                <ToggleButton value="archive" aria-label="archive">
                                    <ArchiveIcon sx={toggleIcon} />
                                    Archive
                                </ToggleButton>
                            </ToggleButtonGroup>
                            <Alert severity="info">
                                Private Groups are only seen by Account Administrators. Public Groups are visible to your Contacts as opt-in/opt-out options if they choose to Manage Preferences.
                            </Alert>
                            <div>
                                <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                                    <Typography>
                                        Tag this group
                                    </Typography>
                                    <Tooltip title="Add New Tag">
                                        <IconButton
                                            onClick={() => setShowDialog(true)}
                                            color="primary"
                                            size="small"
                                        >
                                            <AddCircleIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                                <Table size="small">
                                    <TableBody>
                                        <TableRow>
                                            <Picker
                                                availableItems={tags}
                                                selectedItems={selectedTags}
                                                isTags={true}
                                                onChangeItems={selectedTags => setSelectedTags(selectedTags)}
                                                border={false}
                                            />
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </div>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button type="submit" disabled={!isValid}>Save Changes</Button>
                        <Button variant="outlined" onClick={onClose}>Cancel</Button>
                    </DialogActions>
                </form>
            </Dialog>
            {showDialog && (
                <CreateTagDialog
                    onClose={() => setShowDialog(false)}
                    onSuccess={() => setShowDialog(false)}
                />
            )}
        </>
    );
};

export default EditGroupDialog;